import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    AlertTriangle,
    Chrome,
    KeyRound,
    Mail,
    Ban,
    ShieldCheck,
    Shield,
    Check,
    UserMinus,
} from "lucide-react";
import FilterBar from "../../components/primitives/FilterBar";
import DataTable from "../../components/primitives/DataTable";
import TextButton from "../../components/primitives/TextButton";
import { admin, ApiError } from "../../services/api";
import { relativeTime } from "../../utils/auditFormat";
import "../../styles/admin-shell.css";
import "./AdminUsersPage.css";

const PAGE_SIZE = 50;

const CHIPS = [
    { key: "is_verified", label: "Verified" },
    { key: "is_admin", label: "Admins" },
    { key: "is_disabled", label: "Disabled" },
    { key: "has_password", label: "Password" },
    { key: "has_google", label: "Google" },
];

function readFiltersFromUrl(params) {
    const out = { search: params.get("email") || "" };
    for (const c of CHIPS) {
        const v = params.get(c.key);
        if (v === "true") out[c.key] = true;
    }
    out.offset = parseInt(params.get("offset") || "0", 10) || 0;
    return out;
}

function buildSearchParams(filters) {
    const sp = new URLSearchParams();
    if (filters.search) sp.set("email", filters.search);
    for (const c of CHIPS) {
        if (filters[c.key]) sp.set(c.key, "true");
    }
    if (filters.offset) sp.set("offset", String(filters.offset));
    return sp;
}

function AdminUsersPage() {
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const [filters, setFilters] = useState(() => readFiltersFromUrl(params));
    const [data, setData] = useState({ users: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Sync state ↔ URL
    useEffect(() => {
        setParams(buildSearchParams(filters), { replace: true });
    }, [filters, setParams]);

    // Fetch
    useEffect(() => {
        let cancelled = false;
        const ctrl = new AbortController();
        setLoading(true);
        setError(null);
        const query = {
            limit: PAGE_SIZE,
            offset: filters.offset || 0,
        };
        if (filters.search) query.email = filters.search;
        for (const c of CHIPS) {
            if (filters[c.key]) query[c.key] = "true";
        }
        (async () => {
            try {
                const res = await admin.listUsers(query, { signal: ctrl.signal });
                if (!cancelled) setData(res);
            } catch (err) {
                if (cancelled || err?.name === "AbortError") return;
                setError(err instanceof ApiError ? err.message : err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            ctrl.abort();
        };
    }, [filters]);

    const setSearch = (v) =>
        setFilters((f) => ({ ...f, search: v, offset: 0 }));
    const toggleChip = (key) =>
        setFilters((f) => ({ ...f, [key]: !f[key], offset: 0 }));
    const goPrev = () =>
        setFilters((f) => ({
            ...f,
            offset: Math.max(0, (f.offset || 0) - PAGE_SIZE),
        }));
    const goNext = () =>
        setFilters((f) => ({
            ...f,
            offset: (f.offset || 0) + PAGE_SIZE,
        }));

    const total = data.total || 0;
    const offset = filters.offset || 0;
    const showingFrom = data.users.length === 0 ? 0 : offset + 1;
    const showingTo = offset + data.users.length;
    const hasNext = offset + PAGE_SIZE < total;
    const hasPrev = offset > 0;

    const chips = useMemo(
        () =>
            CHIPS.map((c) => ({ ...c, active: !!filters[c.key] })),
        [filters]
    );

    const columns = useMemo(
        () => [
            {
                key: "email",
                label: "Email",
                width: "minmax(220px, 2fr)",
                render: (u) => (
                    <span className="users__email-cell">
                        <span className="users__email">{u.email || "—"}</span>
                        {u.name && (
                            <span className="users__name">{u.name}</span>
                        )}
                    </span>
                ),
            },
            {
                key: "methods",
                label: "Methods",
                width: "minmax(140px, 1fr)",
                render: (u) => (
                    <span className="users__methods">
                        {u.has_google && (
                            <span className="users__method" title="Google">
                                <Chrome size={11} aria-hidden="true" />
                                Google
                            </span>
                        )}
                        {u.has_password && (
                            <span className="users__method" title="Password">
                                <KeyRound size={11} aria-hidden="true" />
                                Password
                            </span>
                        )}
                        {!u.has_google && !u.has_password && (
                            <span className="users__method" title="Magic link">
                                <Mail size={11} aria-hidden="true" />
                                Magic
                            </span>
                        )}
                    </span>
                ),
            },
            {
                key: "status",
                label: "Status",
                width: "minmax(140px, 0.8fr)",
                render: (u) => {
                    const badges = [];
                    if (u.is_admin)
                        badges.push(
                            <span key="admin" className="users__badge users__badge--accent">
                                <Shield size={10} aria-hidden="true" />
                                Admin
                            </span>
                        );
                    if (u.is_email_verified)
                        badges.push(
                            <span key="verified" className="users__badge users__badge--success">
                                <Check size={10} aria-hidden="true" />
                                Verified
                            </span>
                        );
                    else
                        badges.push(
                            <span key="unverified" className="users__badge users__badge--muted">
                                Unverified
                            </span>
                        );
                    if (u.is_disabled)
                        badges.push(
                            <span key="disabled" className="users__badge users__badge--danger">
                                <Ban size={10} aria-hidden="true" />
                                Disabled
                            </span>
                        );
                    if (u.is_anonymized)
                        badges.push(
                            <span key="anon" className="users__badge users__badge--muted">
                                <UserMinus size={10} aria-hidden="true" />
                                Anonymised
                            </span>
                        );
                    return <span className="users__badges">{badges}</span>;
                },
            },
            {
                key: "tier",
                label: "Tier",
                width: "minmax(70px, 0.4fr)",
                render: (u) => (
                    <span
                        className={`users__badge ${
                            u.tier && u.tier !== "free"
                                ? "users__badge--accent"
                                : "users__badge--muted"
                        }`}
                    >
                        {(u.tier || "free").toUpperCase()}
                    </span>
                ),
            },
            {
                key: "pack_count",
                label: "Packs",
                width: "minmax(70px, 0.4fr)",
                render: (u) => (
                    <span className="mono-num">{u.pack_count ?? 0}</span>
                ),
            },
            {
                key: "last_login_at",
                label: "Last login",
                width: "minmax(120px, 0.6fr)",
                render: (u) =>
                    u.last_login_at ? (
                        <span className="mono">{relativeTime(u.last_login_at)}</span>
                    ) : (
                        <span className="users__empty">Never</span>
                    ),
            },
            {
                key: "created_at",
                label: "Created",
                width: "minmax(120px, 0.6fr)",
                render: (u) => (
                    <span className="mono">{relativeTime(u.created_at)}</span>
                ),
            },
        ],
        []
    );

    return (
        <div className="admin-page">
            <header className="admin-page__header">
                <div className="admin-page__title-block">
                    <span className="admin-page__eyebrow uppercase-label">
                        Admin / Users
                    </span>
                    <h1 className="admin-page__title">Users</h1>
                    <p className="admin-page__subtitle">
                        Search, filter, and open a user to manage them.
                    </p>
                </div>
            </header>

            <div className="admin-page__body">
                <FilterBar
                    search={filters.search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Search by email"
                    chips={chips}
                    onChipToggle={toggleChip}
                />

                {error && (
                    <div className="admin-error" role="alert">
                        <AlertTriangle size={14} aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                )}

                <DataTable
                    columns={columns}
                    rows={data.users}
                    loading={loading}
                    empty={
                        filters.search || chips.some((c) => c.active)
                            ? "No users match these filters."
                            : "No users yet."
                    }
                    onRowClick={(u) => navigate(`/admin/users/${u.id}`)}
                />

                <div className="admin-pagination">
                    <span>
                        {total === 0
                            ? "0 users"
                            : `${showingFrom}–${showingTo} of ${total}`}
                    </span>
                    <div className="admin-pagination__buttons">
                        <TextButton
                            variant="ghost"
                            size="sm"
                            onClick={goPrev}
                            disabled={!hasPrev || loading}
                        >
                            Prev
                        </TextButton>
                        <TextButton
                            variant="ghost"
                            size="sm"
                            onClick={goNext}
                            disabled={!hasNext || loading}
                        >
                            Next
                        </TextButton>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminUsersPage;
